import { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const skyVertex = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

const skyFragment = `
varying vec2 vUv;
uniform float uTime;

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.,0.)), u.x),
             mix(hash(i + vec2(0.,1.)), hash(i + vec2(1.,1.)), u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0; float a = 0.5;
  for (int i = 0; i < 4; i++) { v += a * noise(p); p *= 2.05; a *= 0.5; }
  return v;
}

void main() {
  vec2 uv = vUv;
  float t = uTime * 0.02;

  vec3 ivory = vec3(0.961, 0.941, 0.910);
  vec3 sage = vec3(0.478, 0.620, 0.525);
  vec3 forest = vec3(0.102, 0.302, 0.220);
  vec3 amber = vec3(0.85, 0.62, 0.32);

  float grad = smoothstep(0.0, 1.15, uv.y);
  vec3 col = mix(forest * 1.35, ivory, grad);
  col = mix(col, sage, 0.18 * (1.0 - uv.y) * (0.5 + 0.5 * sin(t * 3.0)));

  vec2 sunPos = vec2(0.68, 0.78);
  float d = distance(uv * vec2(1.6, 1.0), sunPos * vec2(1.6, 1.0));
  float breath = 0.5 + 0.5 * sin(uTime * 0.35);
  float glow = exp(-d * 3.45) * (0.38 + 0.17 * breath);
  col += amber * glow;

  vec2 dir = uv - sunPos;
  float ang = atan(dir.y, dir.x);
  float rays = pow(max(0.0, sin(ang * 7.0 + t * 4.0)), 3.0) * exp(-d * 2.15) * 0.10;
  rays += pow(max(0.0, sin(ang * 11.0 - t * 3.0)), 4.0) * exp(-d * 2.55) * 0.07;
  col += amber * rays * (0.62 + 0.24 * breath);

  float fog = fbm(uv * 3.0 + vec2(t * 2.2, t * 0.7));
  float fogBand = smoothstep(0.55, 0.0, uv.y) * 0.4;
  col = mix(col, ivory, fog * fogBand);

  float grain = hash(uv * 900.0 + uTime) * 0.025;
  col += grain - 0.0125;

  gl_FragColor = vec4(col, 1.0);
}`;

const Sky = () => {
  const mat = useRef();
  useFrame((state) => {
    if (mat.current) mat.current.uniforms.uTime.value = state.clock.elapsedTime;
  });
  const uniforms = useMemo(() => ({ uTime: { value: 0 } }), []);
  return (
    <mesh position={[0, 0, -5]}>
      <planeGeometry args={[22, 12]} />
      <shaderMaterial ref={mat} vertexShader={skyVertex} fragmentShader={skyFragment} uniforms={uniforms} />
    </mesh>
  );
};

const Dust = ({ count = 220 }) => {
  const points = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 14;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 8;
      arr[i * 3 + 2] = Math.random() * -4;
    }
    return arr;
  }, [count]);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    const pos = points.current.geometry.attributes.position.array;
    for (let i = 0; i < count; i++) {
      pos[i * 3 + 1] += Math.sin(t * 0.12 + i) * 0.0009 + 0.0011;
      pos[i * 3] += Math.cos(t * 0.09 + i * 0.7) * 0.0008;
      if (pos[i * 3 + 1] > 4.2) pos[i * 3 + 1] = -4.2;
    }
    points.current.geometry.attributes.position.needsUpdate = true;
    points.current.material.opacity = 0.4 + Math.sin(t * 0.3) * 0.15;
  });

  return (
    <points ref={points}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial size={0.035} color="#F5F0E8" transparent opacity={0.5} blending={THREE.AdditiveBlending} depthWrite={false} />
    </points>
  );
};

const HeroScene = () => {
  const reduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return (
    <Canvas
      dpr={[1, 1.5]}
      camera={{ position: [0, 0, 5], fov: 55 }}
      gl={{ antialias: false, powerPreference: 'high-performance' }}
      frameloop={reduced ? 'demand' : 'always'}
      style={{ position: 'absolute', inset: 0 }}
    >
      <Sky />
      <Dust />
    </Canvas>
  );
};

export default HeroScene;
